import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Loader2, Mail, Phone, Building2 } from "lucide-react";
import { Reveal, Slug } from "@/components/minka/Motion";
import { Seo } from "@/components/minka/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const Admin = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(state?.user || null);
  const [checking, setChecking] = useState(!state?.user);
  const [inquiries, setInquiries] = useState(null);

  useEffect(() => {
    if (state?.user) return;
    // Server-side session verification (source of truth)
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((res) => setUser(res.data))
      .catch(() => setUser(null))
      .finally(() => setChecking(false));
  }, [state]);

  useEffect(() => {
    if (!user) return;
    axios
      .get(`${API}/admin/inquiries`, { withCredentials: true })
      .then((res) => setInquiries(res.data))
      .catch((e) => {
        if (e.response?.status === 403) toast.error("This Google account is not the site owner.");
        setInquiries([]);
      });
  }, [user]);

  const signIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const signOut = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch (e) {}
    setUser(null);
    setInquiries(null);
  };

  if (checking) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center pt-24" data-testid="admin-loading">
        <Loader2 size={20} className="animate-spin text-[#C81D25]" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex min-h-[80vh] flex-col items-center justify-center gap-8 px-5 text-center" data-testid="admin-login">
        <p className="font-display text-4xl text-[#E2E0D8]">Owner access</p>
        <p className="max-w-md text-sm leading-relaxed text-[#A3ACBB]">Sign in with Google to view film-rights inquiries submitted through this site.</p>
        {state?.authError && <p className="text-sm text-[#E76F51]">Sign-in failed. Please try again.</p>}
        <button onClick={signIn} data-testid="google-signin-btn" className="flex items-center gap-3 rounded-full bg-[#E2E0D8] px-8 py-4 font-mono-s text-xs uppercase tracking-[0.25em] text-[#07080B] transition-colors duration-300 hover:bg-[#C81D25] hover:text-[#E2E0D8]">
          <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true"><path fill="currentColor" d="M21.35 11.1h-9.17v2.73h6.51c-.33 3.81-3.5 5.44-6.5 5.44C8.36 19.27 5 16.25 5 12c0-4.1 3.2-7.27 7.2-7.27 3.09 0 4.9 1.97 4.9 1.97L19 4.72S16.56 2 12.1 2C6.42 2 2.03 6.8 2.03 12c0 5.05 4.13 10 10.22 10 5.35 0 9.25-3.67 9.25-9.09 0-1.15-.15-1.81-.15-1.81Z"/></svg>
          Sign in with Google
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1400px] px-5 pb-32 pt-32 md:px-10 md:pt-40" data-testid="admin-page">
      <Seo title="Owner Access — MINKA" description="Owner sign-in." path="/admin" noindex />
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <Slug>Owner dashboard</Slug>
          <h1 className="mt-4 font-display text-4xl font-bold text-[#E2E0D8] sm:text-5xl">Film-rights inquiries</h1>
          <p className="mt-2 text-sm text-[#A3ACBB]">Signed in as {user.email}</p>
        </div>
        <button onClick={signOut} data-testid="admin-signout-btn" className="flex items-center gap-2 rounded-full border border-white/25 px-6 py-3 font-mono-s text-xs uppercase tracking-[0.2em] text-[#E2E0D8] transition-colors duration-300 hover:border-[#C81D25] hover:text-[#E76F51]">
          <LogOut size={14} /> Sign out
        </button>
      </div>

      <div className="mt-14 space-y-4" data-testid="admin-inquiries">
        {inquiries === null && <Loader2 size={20} className="animate-spin text-[#C81D25]" />}
        {inquiries?.length === 0 && <p className="font-serif-i text-2xl text-[#A3ACBB]">No inquiries yet.</p>}
        {inquiries?.map((q, i) => (
          <Reveal key={q.id || i} delay={Math.min(i * 0.05, 0.3)}>
            <div data-testid={`admin-inquiry-${q.id || i}`} className="border border-white/10 bg-[#0E1017]/85 p-6 backdrop-blur-md">
              <div className="flex flex-wrap items-baseline justify-between gap-3">
                <p className="font-display text-2xl text-[#E2E0D8]">{q.full_name}</p>
                <span className="rounded-full border border-[#C81D25]/50 px-3 py-1 font-mono-s text-[10px] uppercase tracking-[0.2em] text-[#E76F51]">{q.inquiry_type.replace("_", " ")}</span>
              </div>
              <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#C3CAD5]">
                {q.company && <span className="flex items-center gap-2"><Building2 size={13} className="text-[#C81D25]" />{q.company}</span>}
                <a href={`mailto:${q.email}`} className="flex items-center gap-2 link-underline"><Mail size={13} className="text-[#C81D25]" />{q.email}</a>
                {q.phone && <a href={`tel:${q.phone}`} className="flex items-center gap-2 link-underline"><Phone size={13} className="text-[#C81D25]" />{q.phone}</a>}
              </div>
              <p className="mt-4 text-sm leading-relaxed text-[#C3CAD5] md:text-base">{q.message}</p>
              <p className="mt-4 font-mono-s text-[10px] uppercase tracking-[0.2em] text-[#A3ACBB]">{new Date(q.created_at).toLocaleString()}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  );
};

export default Admin;
